/**
 * Service Modal Component - Using FormModal with Unsaved Changes Detection
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { CategoryTreeSelector } from '../../components/ui/CategoryTreeSelector';
import { CurrencyInput } from '../../components/form/MaskedInputs';
import { useInstance } from '../../contexts/InstanceContext';
import { toMinor } from '../../lib/currency';

const serviceSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo'),
  description: z.string().max(500, 'Descrição muito longa').optional(),
  price: z.number().min(0, 'Preço deve ser positivo'),
  category_id: z.string().min(1, 'Categoria é obrigatória'),
  sku: z.string().max(50, 'SKU muito longo').optional(),
  is_active: z.boolean().optional(),
});

export function ServiceModal({ open, onOpenChange, service, categories, onSave, loading }) {
  const { t } = useTranslation();
  const { instanceSettings } = useInstance();
  const isEditing = !!service?.id;

  const { register, handleSubmit, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      category_id: '',
      sku: '',
      is_active: true,
    },
  });

  // Reset form when modal opens or service changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (service) {
      reset({
        name: service.name || '',
        description: service.description || '',
        price: service.price || 0,
        category_id: service.category_id || '',
        sku: service.sku || '',
        is_active: service.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        description: '',
        price: 0,
        category_id: '',
        sku: '',
        is_active: true,
      });
    }
  }, [open, service, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    // Convert price to minor units (cents)
    const dataWithMinor = {
      ...data,
      price_minor: toMinor(data.price),
    };
    delete dataWithMinor.price;
    onSave(dataWithMinor);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Serviço' : 'Novo Serviço'}
      description={isEditing ? 'Atualize as informações do serviço' : 'Preencha os dados do novo serviço'}
      size="default"
      formId="service"
      entityId={service?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Nome do serviço"
            data-testid="service-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Descrição do serviço"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="price">Preço *</Label>
            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  id="price"
                  value={field.value}
                  onValueChange={field.onChange}
                  currency={instanceSettings?.currency || 'BRL'}
                  decimalPlaces={instanceSettings?.decimal_places || 2}
                  data-testid="service-price-input"
                />
              )}
            />
            {errors.price && <p className="text-sm text-destructive">{errors.price.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category_id">Categoria *</Label>
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <CategoryTreeSelector
                  categories={categories}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Selecione uma categoria..."
                />
              )}
            />
            {errors.category_id && <p className="text-sm text-destructive">{errors.category_id.message}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sku">Código (SKU)</Label>
          <Input
            id="sku"
            {...register('sku')}
            placeholder="Código do serviço"
          />
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default ServiceModal;
